import { Box, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { INominalSummaryResponse } from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import FileUploadService from '../../../../shared/services/file-upload/file-upload.service'
import { pluralise } from '../../../../shared/utils/nominal-code-utils'
import { useNominalCodeMapping, useNominalReferenceData } from '../../../../hooks/use-nominal-code-mapping'
import { useUnsavedChangesWarning } from '../../../../hooks/use-unsaved-changes-warning'
import { NominalCodeMappingWorkspace, NominalCodeSaveBar } from './nominal-code-mapping-workspace'

/**
 * NominalCodeUploadMappingSection
 * -------------------------------------------------------------------------
 * The Chart of Accounts table inline on the import wizard's validation step, scoped to the codes
 * in one spend upload. Same table, filters, suggestions and bulk bar as the settings page (through
 * useNominalCodeMapping and NominalCodeMappingWorkspace); no delete, because saved outcomes are
 * managed on the page. Saving writes the outcomes, then re-runs the upload's validation so its rows
 * pick them up, then asks the step to refresh.
 *
 * The step owns the summary (it also drives the step's prompt about codes still to map), so this
 * reads it rather than fetching its own. Mounted the first time the section is opened, so the
 * activity lists are not loaded for an import that never opens it.
 */
export const NominalCodeUploadMappingSection: FunctionComponent<{
  fileUploadId: string,
  // getSummary({ fileUploadId }), loaded by the step.
  summary: INominalSummaryResponse | null,
  isLoading: boolean,
  // After a save: re-fetch the step's rows and counts.
  onSaved: () => void,
}> = ({ fileUploadId, summary, isLoading, onSaved }) => {
  //Services
  const fileUploadService = new FileUploadService()
  //Shared state
  const reference = useNominalReferenceData()
  const mapping = useNominalCodeMapping({
    summary,
    activityConversions: reference.activityConversions,
    // Every code here was seen on this upload's lines, so there is nothing to hide.
    defaultShowNoSpend: true,
    allowClearSaved: false,
  })

  useUnsavedChangesWarning(mapping.hasUnsaved)

  const saveHandler = () => mapping.save({
    source: 'import-wizard',
    // The import reads outcomes when it validates, so it is checked again with the new ones.
    afterWrite: () => fileUploadService.reRunValidation(fileUploadId),
    afterWriteFailedMessage: 'This import could not be checked again with them, so refresh the page to see its latest results.',
    describeSaved: (count) => `${pluralise(count, 'code', 'codes')} saved and applied to this import.`,
    onSaved,
  })

  return (
    <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'lightGrey.main', borderRadius: '10px' }}>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2, maxWidth: 680 }}>
        Give each code in this upload an outcome: categorise its spend against an activity, or exclude it.
        Saving applies the outcomes to this import and every future one.
      </Typography>
      <NominalCodeMappingWorkspace
        mapping={mapping}
        reference={reference}
        isLoading={isLoading}
        hasSummary={!!summary}
        showNoSpendToggle={false}
      />
      <NominalCodeSaveBar
        counts={mapping.footerCounts}
        isSaving={mapping.isSaving}
        onSave={saveHandler}
        onDiscard={mapping.discardHandler}
        saveLabel='Save and apply to this import'
      />
    </Box>
  )
}
